import { inject, Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { NzUploadFile, NzUploadChangeParam } from 'ng-zorro-antd/upload';
import { from, Observable, Observer, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { Constants } from '@models/index';

import { FileTemporal } from '@models/index';
import { StringDto } from '@models/basic/string-dto';

import { HttpService } from '@services/http.service';

import { environment } from '@environments/environment';
import { NotificationService } from '@services/utils/notification.service';

export interface FileUploadValidationConfig {
    allowedMimeTypes: string[];
    allowedExtensions: string[];
    invalidFormatTranslationKey: string;
    uploadErrorTranslationKey: string;
    fallbackFileName?: string;
    maxSizeInBytes?: number;
    maxSizeTranslationKey?: string;
}

export type FileUploadResult = { status: 'success'; temporalFile: FileTemporal } | { status: 'invalid-format' } | { status: 'max-size' } | { status: 'error' };

@Injectable({
    providedIn: 'root'
})
export class FilesService extends HttpService {
    private readonly translateService = inject(TranslateService);
    private readonly notificationService = inject(NotificationService);

    private temporal!: FileTemporal | null;
    fileList: NzUploadFile[] = [];
    readonly url: string = `${environment.apps.utilities}/v1/files/temporal`;
    loading = false;
    maxFileSize = 1048576;

    public setTemporal(temporal: any): void {
        this.temporal = new FileTemporal().build(temporal);
    }

    public getTemporal(): FileTemporal | null {
        return this.temporal;
    }

    public removeTemporal(): void {
        this.temporal = null;
    }

    upload(qqFile: FormData): Observable<any> {
        return this.post('files/temporal', { app: 'utilities', version: 1 }, qqFile, {});
    }

    uploadBase64(base64: StringDto): Observable<any> {
        return this.post('files/temporal/base64', { app: 'utilities', version: 1 }, base64);
    }

    uploadWithValidation(file: NzUploadFile, config: FileUploadValidationConfig): Observable<FileUploadResult> {
        if (!this.isAllowedFile(file, config.allowedMimeTypes, config.allowedExtensions)) {
            this.showError(config.invalidFormatTranslationKey);

            return of({ status: 'invalid-format' });
        }

        if (config.maxSizeInBytes && file.size && file.size > config.maxSizeInBytes) {
            this.showError(config.maxSizeTranslationKey ?? 'max_size_file');

            return of({ status: 'max-size' });
        }

        const rawFile = this.getRawFile(file);
        const uploadFileName = file.name ?? config.fallbackFileName ?? 'upload-file';
        const mimeType = file.type || 'application/octet-stream';

        return from(this.readFileAsBlob(rawFile, mimeType)).pipe(
            switchMap((blob) => {
                const formData = new FormData();
                formData.append('qqfile', blob, uploadFileName);

                return this.upload(formData).pipe(
                    map((response) => ({ status: 'success', temporalFile: new FileTemporal().build(response) }) as FileUploadResult)
                );
            }),
            catchError(() => {
                this.showError(config.uploadErrorTranslationKey);

                return of({ status: 'error' } as FileUploadResult);
            })
        );
    }

    // Pre-reads the file into memory so XHR doesn't depend on the underlying URI.
    // Required for Android WebView when files come from providers like Google Drive,
    // whose content:// URIs may be invalidated before the multipart body is read.
    private readFileAsBlob(file: Blob, mimeType: string): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const buffer = reader.result as ArrayBuffer;
                resolve(new Blob([buffer], { type: mimeType }));
            };
            reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
            reader.onabort = () => reject(new Error('FileReader aborted'));
            reader.readAsArrayBuffer(file);
        });
    }

    validateFile = (file: NzUploadFile): Observable<boolean> =>
        new Observable((observer: Observer<boolean>) => {
            if (file != null && file.size != null && file.size > this.maxFileSize) {
                this.translateService.get(['max_size_file'], { maxFileSize: `${this.maxFileSize / 1024 / 1024}MB` }).subscribe(() => {
                    observer.next(false);
                    observer.complete();
                });
            } else {
                observer.next(true);
                observer.complete();
            }
        });

    changeFile(event: NzUploadChangeParam): void {
        switch (event.type) {
            case Constants.UPLOAD.EVENT.START:
                if (this.fileList.length > 1) {
                    this.fileList = this.fileList.slice(1, 2);
                }
                this.loading = true;
                break;
            case Constants.UPLOAD.EVENT.SUCCESS:
                this.temporal = new FileTemporal().build(event.file.response);
                this.loading = false;
                break;
            case Constants.UPLOAD.EVENT.REMOVED:
                this.temporal = null;
                this.loading = false;
                break;
        }
    }

    public getHeaders(token: string, idCenter: number): any {
        return [
            { name: 'X-accesstoken', value: token },
            { name: 'X-idcenter', value: idCenter }
        ];
    }

    public resetData(): void {
        this.fileList = [];
        this.removeTemporal();
    }

    private isAllowedFile(file: NzUploadFile, allowedMimeTypes: string[], allowedExtensions: string[]): boolean {
        const mimeType = (file.type ?? '').toLowerCase();
        const fileName = (file.name ?? '').toLowerCase();
        const normalizedMimeTypes = allowedMimeTypes.map((type) => type.toLowerCase());
        const normalizedExtensions = allowedExtensions.map((extension) => extension.toLowerCase());

        return normalizedMimeTypes.includes(mimeType) || normalizedExtensions.some((extension) => fileName.endsWith(extension));
    }

    private getRawFile(file: NzUploadFile): File {
        return (file as NzUploadFile & { originFileObj?: File }).originFileObj ?? (file as unknown as File);
    }

    private showError(translationKey: string): void {
        this.translateService.get([translationKey]).subscribe((translates) => {
            this.notificationService.showErrorMessage(translates[translationKey]);
        });
    }
}
